import { FocusEvent, MouseEvent, useRef, useState } from "react";
import { useTranslation } from "@localization/useTranslation";
import { setFirstLetterToLowerCase } from "@common/stringOperations";
import { FormulaOperator, FormulaToken, FormulaTokenKind } from "../../EntityTemplate.types.ts";
import { InputFieldTypeEnum } from "../../inputFields/InputFields.types.ts";
import {
    addOperator,
    allowedNext,
    bracketToken,
    caretAfterRemoval,
    describeFormula,
    describeToken as describeOperatorOrBracket,
    evaluateSample,
    fieldToken,
    findFormulaError,
    findMatchingBracket,
    findMisplacedTokenIndexes,
    FormulaError,
    formulaFieldTypes,
    formulaOperators,
    getSampleOperandValue,
    hasOperatorBefore,
    insertAt,
    isReplaceable,
    operatorSymbols,
    operatorToken,
    removeField,
    removeTokens,
    replaceToken,
} from "./calculatedField.utils.ts";
import { FormulaFieldOption } from "./computedField.types.ts";
import { getPeriodUnitTranslationKey } from "./datePeriod.utils.ts";

export interface FormulaEditorProps {
    fieldName: string;
    fieldDescription: string | undefined;
    tokens: Array<FormulaToken>;
    formulaFields: Array<FormulaFieldOption>;
    onFormulaChange: (tokens: Array<FormulaToken>) => void;
}

export interface TokenRectangle {
    top: number;
    bottom: number;
    left: number;
    right: number;
}

interface TokenRow {
    centre: number;
    rectangles: Array<TokenRectangle>;
}

// The symbols a keyboard offers for each operator; x and the Unicode signs are included because * is awkward on a Danish keyboard
const typedOperators: Record<string, FormulaOperator> = {
    "+": FormulaOperator.Add,
    "-": FormulaOperator.Subtract,
    "−": FormulaOperator.Subtract,
    "*": FormulaOperator.Multiply,
    x: FormulaOperator.Multiply,
    X: FormulaOperator.Multiply,
    "×": FormulaOperator.Multiply,
    "/": FormulaOperator.Divide,
    ":": FormulaOperator.Divide,
    "÷": FormulaOperator.Divide,
};

export function useFormulaEditor(props: FormulaEditorProps) {
    const { translateString, translateNumber } = useTranslation();

    // The formula is kept here while it is incomplete, since the backend only accepts complete formulas
    const [draft, setDraft] = useState(props.tokens);
    // The number of tokens before the caret, so inserting and removing happen where the user put it
    const [caret, setCaret] = useState(props.tokens.length);
    const [hasLeftEditor, setHasLeftEditor] = useState(false);
    const [replacing, setReplacing] = useState<{ index: number; anchor: HTMLElement }>();
    const [isFieldPickerOpen, setFieldPickerOpen] = useState(false);
    const tokenBoxRef = useRef<HTMLDivElement>(null);
    const addFieldAnchorRef = useRef<HTMLDivElement>(null);

    const formulaError = findFormulaError(draft);
    const allowedNextTokens = allowedNext(draft, caret);
    // The operator before the caret can be swapped for another one, so the operator buttons stay enabled after it
    const operatorsEnabled = allowedNextTokens.operator || hasOperatorBefore(draft, caret);
    const misplacedIndexes = findMisplacedTokenIndexes(draft);
    const highlightedBracketIndexes = findHighlightedBrackets();
    const fieldOptions = [...props.formulaFields]
        .sort((first, second) => formulaFieldTypes.indexOf(first.type) - formulaFieldTypes.indexOf(second.type) || first.name.localeCompare(second.name))
        .map((field) => ({ id: field.id, name: field.name, typeName: translateString(setFirstLetterToLowerCase(InputFieldTypeEnum[field.type])) }));

    return {
        chips: draft.map(toChip),
        caret,
        tokenBoxRef,
        addFieldAnchorRef,
        fieldOptions,
        replaceMenu: replacing && { anchor: replacing.anchor, options: getReplaceOptions(draft[replacing.index]) },
        closeReplaceMenu: () => setReplacing(undefined),
        replaceWith,
        isFieldPickerOpen,
        openFieldPicker: () => setFieldPickerOpen(true),
        closeFieldPicker: () => setFieldPickerOpen(false),
        allowedNextTokens,
        operatorsEnabled,
        incompleteHint: getIncompleteHint(),
        showIncompleteAsError: hasLeftEditor && formulaError !== undefined,
        example: getExample(),
        addField,
        addOperator: addOperatorAtCaret,
        addBracket: (open: boolean) => insert(bracketToken(open)),
        isEmpty: draft.length === 0,
        clearFormula: () => changeDraft([], 0),
        // The toolbar button always works on the end of the formula; removing at the caret is what Backspace and Delete are for
        removeLastToken: () => removeTokenAt(draft.length - 1),
        placeCaret,
        keepFocusInTokenBox,
        onKeyDown,
        onBlur,
    };

    function toChip(token: FormulaToken, index: number) {
        const isField = token.kind === FormulaTokenKind.Field;
        return {
            index,
            text: describeToken(token),
            isField,
            onDelete: isField ? () => remove(removeField(draft, index)) : undefined,
            // The click stays on the token, or the box behind it would move the caret as well
            onClick: isReplaceable(token)
                ? (event: MouseEvent<HTMLElement>) => {
                      event.stopPropagation();
                      setReplacing({ index, anchor: event.currentTarget });
                  }
                : undefined,
            isMisplaced: misplacedIndexes.includes(index),
            isHighlightedBracket: highlightedBracketIndexes.includes(index),
        };
    }

    function changeDraft(tokens: Array<FormulaToken>, nextCaret: number) {
        setDraft(tokens);
        setCaret(nextCaret);
        setHasLeftEditor(false);
        if (findFormulaError(tokens) === undefined) {
            props.onFormulaChange(tokens);
        }
    }

    function insert(token: FormulaToken) {
        changeDraft(insertAt(draft, caret, token), caret + 1);
    }

    function addOperatorAtCaret(operator: FormulaOperator) {
        changeDraft(addOperator(draft, operator, caret), hasOperatorBefore(draft, caret) ? caret : caret + 1);
    }

    function remove(tokens: Array<FormulaToken>) {
        changeDraft(tokens, caretAfterRemoval(draft, tokens, caret));
    }

    // A bracket goes together with its match, so removing one never leaves the other behind unmatched
    function removeTokenAt(index: number) {
        const match = findMatchingBracket(draft, index);
        remove(removeTokens(draft, match === undefined ? [index] : [index, match]));
    }

    function addField(fieldId: string) {
        setFieldPickerOpen(false);
        const field = props.formulaFields.find((formulaField) => formulaField.id === fieldId);
        if (field) {
            insert(fieldToken(field.id, field.type));
        }
    }

    // The bracket right before the caret is the one Backspace would remove, so it is the one shown with its match; otherwise the one Delete would remove
    function findHighlightedBrackets(): Array<number> {
        for (const index of [caret - 1, caret]) {
            const match = findMatchingBracket(draft, index);
            if (match !== undefined) {
                return [index, match];
            }
        }

        return [];
    }

    function getReplaceOptions(token: FormulaToken) {
        if (token.kind === FormulaTokenKind.Operator) {
            return formulaOperators.map((operator) => ({
                id: operator.toString(),
                name: operatorSymbols[operator],
                hint: undefined,
                isSelected: operator === token.operator,
            }));
        }

        return fieldOptions.map((option) => ({ id: option.id, name: option.name, hint: option.typeName, isSelected: option.id === token.fieldId }));
    }

    function replaceWith(optionId: string) {
        const index = replacing!.index;
        setReplacing(undefined);
        changeDraft(replaceToken(draft, index, toReplacement(draft[index], optionId)), caret);
    }

    function toReplacement(token: FormulaToken, optionId: string) {
        if (token.kind === FormulaTokenKind.Operator) {
            return operatorToken(Number(optionId) as FormulaOperator);
        }

        const field = props.formulaFields.find((formulaField) => formulaField.id === optionId)!;
        return fieldToken(field.id, field.type);
    }

    // Clicking empty space or a bracket puts the caret in the nearest gap
    function placeCaret(event: MouseEvent<HTMLElement>) {
        setCaret(findCaretAtPoint(getTokenRectangles(), event.clientX, event.clientY));
    }

    // Up and Down move the caret straight up or down to the row of tokens above or below it, as in a text field, and to the ends when there is no such row
    function moveCaretToRow(direction: "up" | "down") {
        const caretRectangle = tokenBoxRef.current?.querySelector(".formulaCaret")?.getBoundingClientRect();
        if (caretRectangle) {
            setCaret(findCaretOnAdjacentRow(getTokenRectangles(), caretRectangle, direction));
        }
    }

    function getTokenRectangles(): Array<TokenRectangle> {
        const tokenElements = tokenBoxRef.current?.querySelectorAll<HTMLElement>("[data-token]") ?? [];
        return Array.from(tokenElements, (element) => element.getBoundingClientRect());
    }

    // A toolbar click would otherwise take the focus, hiding the caret and leaving the keys dead until the user clicks back into the box.
    // Keeping it also lets the popovers hand the focus back to the box when they close
    function keepFocusInTokenBox(event: MouseEvent<HTMLElement>) {
        event.preventDefault();
    }

    // Only keys on the formula box itself, so typing in the field picker leaves the formula alone
    function onKeyDown(event: React.KeyboardEvent) {
        if (event.target !== event.currentTarget) {
            return;
        }

        const action = getKeyAction(event.key);
        if (action) {
            event.preventDefault();
            action();
        }
    }

    // Typed symbols follow the same rules as the toolbar buttons, so a key does nothing where its button is disabled
    function getKeyAction(key: string): (() => void) | undefined {
        const operator = typedOperators[key];
        if (operator !== undefined) {
            return operatorsEnabled ? () => addOperatorAtCaret(operator) : undefined;
        }

        switch (key) {
            case "Enter":
                return allowedNextTokens.field ? () => setFieldPickerOpen(true) : undefined;
            case "(":
                return allowedNextTokens.openBracket ? () => insert(bracketToken(true)) : undefined;
            case ")":
                return allowedNextTokens.closeBracket ? () => insert(bracketToken(false)) : undefined;
            case "ArrowLeft":
                return () => setCaret(Math.max(0, caret - 1));
            case "ArrowRight":
                return () => setCaret(Math.min(draft.length, caret + 1));
            case "ArrowUp":
                return () => moveCaretToRow("up");
            case "ArrowDown":
                return () => moveCaretToRow("down");
            case "Home":
                return () => setCaret(0);
            case "End":
                return () => setCaret(draft.length);
            case "Backspace":
                return caret > 0 ? () => removeTokenAt(caret - 1) : undefined;
            case "Delete":
                return caret < draft.length ? () => removeTokenAt(caret) : undefined;
            default:
                return undefined;
        }
    }

    // Incomplete is only shown as an error once focus has left the editor, so building a formula step by step never looks wrong.
    // The replace menu and the field picker open in popovers outside the editor, so moving into them does not count as leaving
    function onBlur(event: FocusEvent<HTMLElement>) {
        if (!replacing && !isFieldPickerOpen && !event.currentTarget.contains(event.relatedTarget as Node | null)) {
            setHasLeftEditor(true);
        }
    }

    function getIncompleteHint() {
        switch (formulaError) {
            case FormulaError.ExpectsOperand:
                return translateString("formulaIncompleteHint");
            case FormulaError.UnclosedBracket:
                return translateString("formulaCloseBracketHint");
            case FormulaError.UnexpectedToken:
                return translateString("formulaMisplacedHint");
            default:
                return undefined;
        }
    }

    function getExample() {
        if (draft.length === 0 || formulaError !== undefined) {
            return undefined;
        }

        const value = evaluateSample(draft);
        const expression = describeFormula(draft, (token) => describeSample(token));
        return value === undefined ? undefined : `${expression} = ${translateNumber(value, { maximumFractionDigits: 2 })}`;
    }

    function describeToken(token: FormulaToken) {
        if (token.kind !== FormulaTokenKind.Field) {
            return describeOperatorOrBracket(token, () => undefined);
        }

        return props.formulaFields.find((field) => field.id === token.fieldId)?.name ?? "";
    }

    function describeSample(token: FormulaToken) {
        const sample = translateNumber(getSampleOperandValue(token.fieldType!));
        if (token.fieldType !== InputFieldTypeEnum.DatePeriodField) {
            return sample;
        }

        const periodUnit = props.formulaFields.find((field) => field.id === token.fieldId)?.periodUnit;
        return `${sample} ${translateString(getPeriodUnitTranslationKey(periodUnit))}`;
    }
}

// The gap nearest a point: on the row whose centre line is closest, after every token whose midpoint lies left of the point
export function findCaretAtPoint(rectangles: Array<TokenRectangle>, x: number, y: number): number {
    const rows = groupIntoRows(rectangles);
    return rows.length === 0 ? 0 : findCaretInRow(rows, findNearestRow(rows, y), x);
}

export function findCaretOnAdjacentRow(rectangles: Array<TokenRectangle>, caretRectangle: TokenRectangle, direction: "up" | "down"): number {
    const rows = groupIntoRows(rectangles);
    const targetRow = findNearestRow(rows, verticalCentre(caretRectangle)) + (direction === "up" ? -1 : 1);
    if (targetRow < 0) {
        return 0;
    }

    return targetRow >= rows.length ? rectangles.length : findCaretInRow(rows, targetRow, caretRectangle.left);
}

// Tokens come in reading order, so a new row begins wherever the next token starts no further right than the one before it.
// Chips and operators differ in height but are centred on their row, so the centre line stands for the row
function groupIntoRows(rectangles: Array<TokenRectangle>): Array<TokenRow> {
    const rows: Array<TokenRow> = [];
    rectangles.forEach((rectangle, index) => {
        const currentRow = rows[rows.length - 1];
        if (currentRow && rectangle.left > rectangles[index - 1].left) {
            currentRow.rectangles.push(rectangle);
        } else {
            rows.push({ centre: verticalCentre(rectangle), rectangles: [rectangle] });
        }
    });

    return rows;
}

function findNearestRow(rows: Array<TokenRow>, y: number): number {
    return rows.reduce((nearest, row, index) => (Math.abs(row.centre - y) < Math.abs(rows[nearest].centre - y) ? index : nearest), 0);
}

function findCaretInRow(rows: Array<TokenRow>, rowIndex: number, x: number): number {
    const tokensOnRowsAbove = rows.slice(0, rowIndex).reduce((count, row) => count + row.rectangles.length, 0);
    return tokensOnRowsAbove + rows[rowIndex].rectangles.filter((rectangle) => (rectangle.left + rectangle.right) / 2 < x).length;
}

function verticalCentre(rectangle: TokenRectangle): number {
    return (rectangle.top + rectangle.bottom) / 2;
}
